import { JSONSchema7 } from "json-schema";
import { CurlRequest } from "./CurlRequest.model";
import { useCallback } from "react";

export function splitSchema(
  curlRequest: CurlRequest,
  mergedSchema: JSONSchema7
): {
  bodySchema: JSONSchema7 | undefined;
  querySchema: JSONSchema7 | undefined;
  pathSchema: JSONSchema7 | undefined;
} {
  const queryKeys = new Set(Object.keys(curlRequest.queryParams || {}));
  const pathKeys = new Set(Object.keys(curlRequest.pathParams || {}));
  const bodyKeys = new Set(
    curlRequest.body &&
    typeof curlRequest.body === "object" &&
    !Array.isArray(curlRequest.body)
      ? Object.keys(curlRequest.body as Record<string, any>)
      : []
  );

  const mergedProperties = mergedSchema.properties || {};
  const mergedRequired = mergedSchema.required || [];

  const bodyProperties: Record<string, JSONSchema7> = {};
  const queryProperties: Record<string, JSONSchema7> = {};
  const pathProperties: Record<string, JSONSchema7> = {};
  const bodyRequired: string[] = [];
  const queryRequired: string[] = [];
  const pathRequired: string[] = [];

  // Split properties based on where they appear in the CurlRequest
  Object.entries(mergedProperties).forEach(([key, schema]) => {
    if (typeof schema === "object" && schema !== null) {
      const jsonSchema = schema as JSONSchema7;

      if (bodyKeys.has(key)) {
        // Property exists in body, assign to body schema
        bodyProperties[key] = jsonSchema;
        if (mergedRequired.includes(key)) {
          bodyRequired.push(key);
        }
      } else if (queryKeys.has(key)) {
        // Property exists in query params, assign to query schema
        queryProperties[key] = jsonSchema;
        if (mergedRequired.includes(key)) {
          queryRequired.push(key);
        }
      } else if (pathKeys.has(key)) {
        // Property exists in path params, assign to path schema
        pathProperties[key] = jsonSchema;
        if (mergedRequired.includes(key)) {
          pathRequired.push(key);
        }
      } else {
        // Property doesn't exist in any, make a best guess based on common patterns
        // Query parameters are typically simple types (string, number, boolean)
        // Body parameters are typically more complex (objects, arrays)
        const isSimpleType =
          jsonSchema.type === "string" ||
          jsonSchema.type === "number" ||
          jsonSchema.type === "integer" ||
          jsonSchema.type === "boolean";

        if (isSimpleType) {
          queryProperties[key] = jsonSchema;
          if (mergedRequired.includes(key)) {
            queryRequired.push(key);
          }
        } else {
          bodyProperties[key] = jsonSchema;
          if (mergedRequired.includes(key)) {
            bodyRequired.push(key);
          }
        }
      }
    }
  });

  // Create body schema if there are body properties
  const bodySchema =
    Object.keys(bodyProperties).length > 0
      ? {
          type: "object" as const,
          properties: bodyProperties,
          required: bodyRequired.length > 0 ? bodyRequired : undefined,
          title: "Request Body",
          description: "Request body parameters",
        }
      : undefined;

  // Create query schema if there are query properties
  const querySchema =
    Object.keys(queryProperties).length > 0
      ? {
          type: "object" as const,
          properties: queryProperties,
          required: queryRequired.length > 0 ? queryRequired : undefined,
          title: "Query Parameters",
          description: "URL query parameters",
        }
      : undefined;

  // Create path schema if there are path properties
  const pathSchema =
    Object.keys(pathProperties).length > 0
      ? {
          type: "object" as const,
          properties: pathProperties,
          required: pathRequired.length > 0 ? pathRequired : undefined,
          title: "Path Parameters",
          description: "URL path parameters",
        }
      : undefined;

  return {
    bodySchema,
    querySchema,
    pathSchema,
  };
}

export function useSplitSchema() {
  return useCallback((curlRequest: CurlRequest, mergedSchema: JSONSchema7) => {
    return splitSchema(curlRequest, mergedSchema);
  }, []);
}
